import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { ChartCardSkeleton } from './ChartCardSkeleton';

/**
 * Wraps a chart Card with prev/next navigation arrows on the left and right edges.
 * Uses react-router-dom <Link> for async (no-page-reload) navigation — exactly like the old Pagination buttons.
 * Touch swipe also navigates via history.push().
 *
 * Shows a skeleton placeholder while loading data or during route-param navigation.
 *
 * @param {string} props.routeSegment - Route segment for pagination URL
 * @param {boolean} props.onlyYear - Navigate by year only
 * @param {object} props.match - React Router match object with params (via withRouterBridge)
 * @param {object} props.history - History object from withRouterBridge
 * @param {React.ReactNode} props.children - The chart Card to wrap
 * @param {boolean} props.loading - Show skeleton while true (parent-controlled, e.g. !isLoaded)
 */
class ChartCardWithPagination extends Component {
    constructor(props) {
        super(props);
        this.state = { swipePos: null, navigating: false };
    }

    // Detect route-param changes → show skeleton during data refetch
    componentDidUpdate(prevProps) {
        const prevYear = prevProps.match?.params?.Year;
        const nextYear = this.props.match?.params?.Year;
        const prevMonth = prevProps.match?.params?.Month;
        const nextMonth = this.props.match?.params?.Month;
        const prevDay = prevProps.match?.params?.Day;
        const nextDay = this.props.match?.params?.Day;

        if (prevYear !== nextYear || prevMonth !== nextMonth || prevDay !== nextDay) {
            this.setState({ navigating: true });
            // Clear after a short debounce — data fetch usually completes within this window
            clearTimeout(this._navTimer);
            this._navTimer = setTimeout(() => this.setState({ navigating: false }), 2000);
        }
    }

    componentWillUnmount() {
        clearTimeout(this._navTimer);
    }

    getPrevLink() {
        const dt = new Date();
        let m = this.props.match?.params?.Month || dt.getMonth();
        if (typeof this.props.match?.params?.Month !== 'undefined' && !isNaN(parseInt(m)) && isFinite(m))
            m = parseInt(m) - 1;
        let y = this.props.match?.params?.Year || dt.getFullYear();
        if (!isNaN(parseInt(y)) && isFinite(y)) y = parseInt(y);

        if (this.props.includeDay) {
            // Day navigation: prev day
            let d = this.props.match?.params?.Day || dt.getDate();
            if (!isNaN(parseInt(d)) && isFinite(d)) d = parseInt(d);
            const prevDate = new Date(y, m, d - 1);
            return '/' + this.props.routeSegment + '/' + prevDate.getFullYear() + '/' + (prevDate.getMonth() + 1) + '/' + prevDate.getDate();
        } else if (this.props.onlyYear) {
            return '/' + this.props.routeSegment + '/' + (y - 1);
        } else {
            const prevDate = new Date(y, m - 1, 1);
            return '/' + this.props.routeSegment + '/' + prevDate.getFullYear() + '/' + (prevDate.getMonth() + 1);
        }
    }

    getNextLink() {
        const dt = new Date();
        let m = this.props.match?.params?.Month || dt.getMonth();
        if (typeof this.props.match?.params?.Month !== 'undefined' && !isNaN(parseInt(m)) && isFinite(m))
            m = parseInt(m) - 1;
        let y = this.props.match?.params?.Year || dt.getFullYear();
        if (!isNaN(parseInt(y)) && isFinite(y)) y = parseInt(y);

        if (this.props.includeDay) {
            // Day navigation: next day
            let d = this.props.match?.params?.Day || dt.getDate();
            if (!isNaN(parseInt(d)) && isFinite(d)) d = parseInt(d);
            const nextDate = new Date(y, m, d + 1);
            return '/' + this.props.routeSegment + '/' + nextDate.getFullYear() + '/' + (nextDate.getMonth() + 1) + '/' + nextDate.getDate();
        } else if (this.props.onlyYear) {
            return '/' + this.props.routeSegment + '/' + (y + 1);
        } else {
            const nextDate = new Date(y, m + 1, 1);
            return '/' + this.props.routeSegment + '/' + nextDate.getFullYear() + '/' + (nextDate.getMonth() + 1);
        }
    }

    // Touch swipe support — use history.push for async navigation
    onTouchStart = (e) => {
        this.setState({ swipePos: e.touches[0].clientX });
    };

    onTouchEnd = (e) => {
        if (this.state.swipePos === null) return;
        const diff = this.state.swipePos - e.changedTouches[0].clientX;
        // Threshold: 50px swipe triggers navigation
        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                this.props.history.push(this.getNextLink());
            } else {
                this.props.history.push(this.getPrevLink());
            }
        }
        this.setState({ swipePos: null });
    };

    render() {
        // Show skeleton during initial load (parent-controlled) or route navigation
        const showSkeleton = this.props.loading || this.state.navigating;

        return (
            <div className="chart-pagination-row">
                <Link to={this.getPrevLink()}
                      className="btn chart-pagination-btn chart-pagination-btn-prev"
                      title="Vorheriger Monat">
                    <i className="bi bi-chevron-left" />
                </Link>

                <div className="chart-card-wrapper"
                     onTouchStart={this.onTouchStart}
                     onTouchEnd={this.onTouchEnd}>
                    {showSkeleton ? <ChartCardSkeleton /> : this.props.children}
                </div>

                <Link to={this.getNextLink()}
                      className="btn chart-pagination-btn chart-pagination-btn-next"
                      title="Nächster Monat">
                    <i className="bi bi-chevron-right" />
                </Link>
            </div>
        );
    }
}

export { ChartCardWithPagination };
